import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { Observable, of } from 'rxjs';
import { filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';

import {
  OperatorTranslations,
  OperatorTranslationRegistry,
  OperatorTranslationSet
} from '../../models/operator-translation.model';
import { ConfigService } from '../config/config.service';

@Injectable({
  providedIn: 'root'
})
export class OperatorTranslationService {

  // Sparse per-operator translation overrides, layered on top of the base
  // public/i18n/{lang}.json files. Sibling registries: OperatorThemeService
  // (styling) and OperatorConfigService (currency etc.) - same bucket
  // folder, same "one file per concern" split.
  //
  // Served from the image gateway bucket (ConfigService.PROXIES.image_gateway),
  // under an "iframe" folder shared with pmu-mobile - both apps fetch this
  // exact same remote file rather than keeping independent local copies.
  private readonly translationsPath = 'iframe/operator_translations.json';

  private translations$: Observable<OperatorTranslationRegistry>;
  private activeOperator: string | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly translate: TranslateService,
    private readonly configService: ConfigService
  ) {
    // Waits for ConfigService.configsLoad$ before fetching -
    // PROXIES.image_gateway isn't populated until loadAppConfigs() resolves.
    this.translations$ = this.configService.configsLoad$.pipe(
      filter(Boolean),
      take(1),
      switchMap(() => this.http.get<OperatorTranslationRegistry>(this.buildTranslationsUrl())),
      map(registry => this.normalizeRegistry(registry)),
      shareReplay(1)
    );

    // ngx-translate's HttpLoader re-fetches the base file on every language
    // switch, which wipes any prior setTranslation() merge - reapply the
    // active operator's override whenever the language changes. Same
    // "reapply on onLangChange" pattern used elsewhere in the app for
    // values baked in once and never refreshed (see my-bets.component.ts).
    this.translate.onLangChange.subscribe(event => {
      if (this.activeOperator) {
        this.applyOverridesForLang(this.activeOperator, event.lang).subscribe();
      }
    });
  }

  /**
   * Apply an operator's translation overrides for the currently active
   * language, and keep reapplying them across later language changes until
   * clearOperatorTranslations() is called.
   */
  applyOperatorTranslations(operatorName: string): Observable<boolean> {

    const normalizedOperator = this.normalizeOperatorName(operatorName);

    this.activeOperator = normalizedOperator;

    const lang = this.translate.currentLang || this.translate.getDefaultLang();

    return this.applyOverridesForLang(normalizedOperator, lang);
  }

  /**
   * Stop reapplying overrides on future language changes.
   *
   * Does not retroactively strip already-merged keys from the current
   * language's translation store (ngx-translate has no targeted "unmerge");
   * a subsequent reloadLang()/page reload resets to the base file.
   */
  clearOperatorTranslations(): void {
    this.activeOperator = null;
  }

  private applyOverridesForLang(operatorName: string, lang: string): Observable<boolean> {

    if (!lang) {
      return of(false);
    }

    return this.translations$.pipe(
      take(1),
      map(registry => registry[operatorName]?.[lang]),
      tap(overrides => {
        if (overrides && Object.keys(overrides).length) {
          this.translate.setTranslation(lang, overrides, true);
        }
      }),
      map(overrides => !!overrides && !!Object.keys(overrides).length)
    );
  }

  private buildTranslationsUrl(): string {
    return `${this.configService.PROXIES.image_gateway}${this.translationsPath}`;
  }

  private normalizeOperatorName(operatorName: string): string {

    return (operatorName || '')
      .trim()
      .toLowerCase();
  }

  private normalizeRegistry(
    registry: OperatorTranslationRegistry
  ): OperatorTranslationRegistry {

    return Object.keys(registry || {}).reduce(
      (result, operator) => {

        result[
          this.normalizeOperatorName(operator)
        ] = this.sanitizeOperatorTranslations(registry[operator]);

        return result;
      },
      {} as OperatorTranslationRegistry
    );
  }

  /**
   * Drop non-string values so a malformed override entry can't inject
   * nested objects/arrays into the ngx-translate store.
   */
  private sanitizeOperatorTranslations(
    langMap: OperatorTranslations
  ): OperatorTranslations {

    if (!langMap || typeof langMap !== 'object') {
      return {};
    }

    return Object.keys(langMap).reduce(
      (result, lang) => {

        const set = langMap[lang];

        if (set && typeof set === 'object') {
          result[lang] = Object.keys(set).reduce(
            (sanitized, key) => {

              if (typeof set[key] === 'string') {
                sanitized[key] = set[key];
              }

              return sanitized;
            },
            {} as OperatorTranslationSet
          );
        }

        return result;
      },
      {} as OperatorTranslations
    );
  }
}
